import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import apiUtils from 'utils/apiutils';

function ApiTable() {
  const [apis, setApis] = useState([]);
  const [page_no, setPageNo] = useState(1);
  const [page_size, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [tradeTypeFilter, setTradeTypeFilter] = useState(['PAPER', 'LIVE']); // Default to both
  const [exchangeTypeFilter, setExchangeTypeFilter] = useState(['SPOT', 'FUTURES']); // Default to both
  const [showFiltersModal, setShowFiltersModal] = useState(false); // For mobile filters modal

  // Dropdown states
  const [showTradeDropdown, setShowTradeDropdown] = useState(false);
  const [showExchangeDropdown, setShowExchangeDropdown] = useState(false);
  const [tempTradeTypes, setTempTradeTypes] = useState([]);
  const [tempExchangeTypes, setTempExchangeTypes] = useState([]);

  const tradeRef = useRef(null);
  const exchangeRef = useRef(null);

  const handleToggleTrade = () => {
    if (showTradeDropdown) {
      setShowTradeDropdown(false);
    } else {
      setTempTradeTypes([...tradeTypeFilter]);
      setShowTradeDropdown(true);
    }
  };

  const handleToggleExchange = () => {
    if (showExchangeDropdown) {
      setShowExchangeDropdown(false);
    } else {
      setTempExchangeTypes([...exchangeTypeFilter]);
      setShowExchangeDropdown(true);
    }
  };

  const handleApplyTrade = () => {
    setTradeTypeFilter(tempTradeTypes);
    setPageNo(1);
    setShowTradeDropdown(false);
  };

  const handleCancelTrade = () => {
    setShowTradeDropdown(false);
  };

  const handleApplyExchange = () => {
    setExchangeTypeFilter(tempExchangeTypes);
    setPageNo(1);
    setShowExchangeDropdown(false);
  };

  const handleCancelExchange = () => {
    setShowExchangeDropdown(false);
  };

  const fetchApis = async (page, size, tradeTypes, exchangeTypes) => {
    const postData = {
      page_no: page,
      page_size: size,
      trade_type: tradeTypes,
      exchange_type: exchangeTypes,
    };

    try {
      const response = await apiUtils.post('/api/view_api_details', postData);
      if (response?.Success) {
        setApis(response.Success);
      }
    } catch (err) {
      // toast.error("Failed to fetch APIs");
    }
  };

  const fetchApisTotal = async (page, size, tradeTypes, exchangeTypes) => {
    const postData = {
      page_no: page,
      page_size: size,
      trade_type: tradeTypes,
      exchange_type: exchangeTypes,
    };

    try {
      const response = await apiUtils.post('/api/total_view_api_details', postData);
      if (response?.Success) {
        setTotalPages(response.Success);
      }
    } catch (err) {
      // Handle error
    }
  };

  useEffect(() => {
    fetchApis(page_no, page_size, tradeTypeFilter, exchangeTypeFilter);
    fetchApisTotal(page_no, page_size, tradeTypeFilter, exchangeTypeFilter);
  }, [page_no, page_size, tradeTypeFilter, exchangeTypeFilter]);

  const handlePageChange = (newPage) => {
    setPageNo(newPage);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page_no - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (page_no > 1) pages.push(<button key="prev" onClick={() => handlePageChange(page_no - 1)} className="px-3 py-2 mx-1 bg-primary text-primary-foreground rounded">‹</button>);
    if (startPage > 1) pages.push(<button key="start-1" onClick={() => handlePageChange(1)} className="px-3 py-2 mx-1 bg-muted text-muted-foreground rounded">1</button>);
    if (startPage > 2) pages.push(<span key="ellipsis-start" className="px-3 py-2 mx-1 text-muted-foreground">...</span>);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 mx-1 rounded ${i === page_no ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages - 1) pages.push(<span key="ellipsis-end" className="px-3 py-2 mx-1 text-muted-foreground">...</span>);
    if (endPage < totalPages) pages.push(<button key="end" onClick={() => handlePageChange(totalPages)} className="px-3 py-2 mx-1 bg-muted text-muted-foreground rounded">{totalPages}</button>);
    if (page_no < totalPages) pages.push(<button key="next" onClick={() => handlePageChange(page_no + 1)} className="px-3 py-2 mx-1 bg-primary text-primary-foreground rounded">›</button>);

    return (
      <div className="flex justify-center items-center space-x-2 mt-6">
        <select
          value={page_size}
          onChange={(e) => {
            setPageSize(parseInt(e.target.value));
            setPageNo(1);
          }}
          className="px-3 py-2 bg-input text-foreground border border-border rounded"
        >
          <option value={10}>10 per page</option>
          <option value={20}>20 per page</option>
          <option value={50}>50 per page</option>
        </select>
        <div className="flex space-x-1">{pages}</div>
        <span className="px-3 py-2 text-muted-foreground">{page_no} / {totalPages}</span>
      </div>
    );
  };

  const renderFiltersMobile = () => (
    <div className="lg:hidden flex justify-end mb-6">
      <button
        onClick={() => setShowFiltersModal(true)}
        className="p-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
      </button>
    </div>
  );

  const renderFiltersModal = () => (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:hidden ${showFiltersModal ? 'block' : 'hidden'}`}
      onClick={() => setShowFiltersModal(false)}
    >
      <div
        className="bg-background p-6 rounded-lg border border-border max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Filters</h3>
          <button
            onClick={() => setShowFiltersModal(false)}
            className="text-muted-foreground hover:text-foreground"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="space-y-6">
          <div>
            <label className="block text-foreground mb-2 font-medium">Trade Type:</label>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 p-2 rounded hover:bg-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={tradeTypeFilter.includes('PAPER')}
                  onChange={(e) => {
                    const newFilters = e.target.checked
                      ? [...tradeTypeFilter, 'PAPER']
                      : tradeTypeFilter.filter((t) => t !== 'PAPER');
                    setTradeTypeFilter(newFilters);
                    setPageNo(1);
                  }}
                  className="rounded"
                />
                <span>Paper</span>
              </label>
              <label className="flex items-center space-x-3 p-2 rounded hover:bg-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={tradeTypeFilter.includes('LIVE')}
                  onChange={(e) => {
                    const newFilters = e.target.checked
                      ? [...tradeTypeFilter, 'LIVE']
                      : tradeTypeFilter.filter((t) => t !== 'LIVE');
                    setTradeTypeFilter(newFilters);
                    setPageNo(1);
                  }}
                  className="rounded"
                />
                <span>Live</span>
              </label>
            </div>
          </div>
          {/* <div>
            <label className="block text-foreground mb-2 font-medium">Exchange Type:</label>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 p-2 rounded hover:bg-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={exchangeTypeFilter.includes('SPOT')}
                  onChange={(e) => {
                    const newFilters = e.target.checked
                      ? [...exchangeTypeFilter, 'SPOT']
                      : exchangeTypeFilter.filter((t) => t !== 'SPOT');
                    setExchangeTypeFilter(newFilters);
                    setPageNo(1);
                  }}
                  className="rounded"
                />
                <span>Spot</span>
              </label>
              <label className="flex items-center space-x-3 p-2 rounded hover:bg-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={exchangeTypeFilter.includes('FUTURES')}
                  onChange={(e) => {
                    const newFilters = e.target.checked
                      ? [...exchangeTypeFilter, 'FUTURES']
                      : exchangeTypeFilter.filter((t) => t !== 'FUTURES');
                    setExchangeTypeFilter(newFilters);
                    setPageNo(1);
                  }}
                  className="rounded"
                />
                <span>Futures</span>
              </label>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );

  const renderTradeDropdown = () => {
    if (!tradeRef.current || !showTradeDropdown) return null;

    const rect = tradeRef.current.getBoundingClientRect();

    return createPortal(
      <div
        style={{
          position: 'fixed',
          left: `${rect.left}px`,
          top: `${rect.bottom}px`,
          zIndex: 9999,
          width: '200px',
        }}
        className="bg-background border border-border rounded-lg shadow-lg p-3"
      >
        <div className="space-y-2 mb-3">
          <label className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer">
            <input
              type="checkbox"
              checked={tempTradeTypes.includes('PAPER')}
              onChange={(e) => {
                const newTemp = e.target.checked
                  ? [...tempTradeTypes, 'PAPER']
                  : tempTradeTypes.filter((t) => t !== 'PAPER');
                setTempTradeTypes(newTemp);
              }}
              className="rounded"
            />
            <span>Paper</span>
          </label>
          <label className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer">
            <input
              type="checkbox"
              checked={tempTradeTypes.includes('LIVE')}
              onChange={(e) => {
                const newTemp = e.target.checked
                  ? [...tempTradeTypes, 'LIVE']
                  : tempTradeTypes.filter((t) => t !== 'LIVE');
                setTempTradeTypes(newTemp);
              }}
              className="rounded"
            />
            <span>Live</span>
          </label>
        </div>
        <div className="flex justify-end pt-2 border-t border-border space-x-2">
          <button
            onClick={handleCancelTrade}
            className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyTrade}
            className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded"
          >
            Apply
          </button>
        </div>
      </div>,
      document.body
    );
  };

  const renderExchangeDropdown = () => {
    if (!exchangeRef.current || !showExchangeDropdown) return null;

    const rect = exchangeRef.current.getBoundingClientRect();

    return createPortal(
      <div
        style={{
          position: 'fixed',
          left: `${rect.left}px`,
          top: `${rect.bottom}px`,
          zIndex: 9999,
          width: '200px',
        }}
        className="bg-background border border-border rounded-lg shadow-lg p-3"
      >
        <div className="space-y-2 mb-3">
          <label className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer">
            <input
              type="checkbox"
              checked={tempExchangeTypes.includes('SPOT')}
              onChange={(e) => {
                const newTemp = e.target.checked
                  ? [...tempExchangeTypes, 'SPOT']
                  : tempExchangeTypes.filter((t) => t !== 'SPOT');
                setTempExchangeTypes(newTemp);
              }}
              className="rounded"
            />
            <span>Spot</span>
          </label>
          <label className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer">
            <input
              type="checkbox"
              checked={tempExchangeTypes.includes('FUTURES')}
              onChange={(e) => {
                const newTemp = e.target.checked
                  ? [...tempExchangeTypes, 'FUTURES']
                  : tempExchangeTypes.filter((t) => t !== 'FUTURES');
                setTempExchangeTypes(newTemp);
              }}
              className="rounded"
            />
            <span>Futures</span>
          </label>
        </div>
        <div className="flex justify-end pt-2 border-t border-border space-x-2">
          <button
            onClick={handleCancelExchange}
            className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyExchange}
            className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded"
          >
            Apply
          </button>
        </div>
      </div>,
      document.body
    );
  };

  const renderTable = () => (
    <div className="hidden lg:block overflow-x-auto shadow-md rounded-lg mb-8 relative">
      <table className="min-w-full rounded-xl border border-border">
        <thead className="bg-muted text-primary">
          <tr>
            <th className="py-3 px-4 text-left text-sm font-semibold">Datetime</th>
            <th className="py-3 px-4 text-left text-sm font-semibold">Api Name</th>
            <th className="py-3 px-4 text-left text-sm font-semibold">Api ID</th>
            <th className="py-3 px-4 text-left text-sm font-semibold">Exchange</th>
            <th ref={tradeRef} className="py-3 px-4 text-left text-sm font-semibold relative group">
              Trade Type
              <button
                onClick={handleToggleTrade}
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted inline-flex items-center"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
              </button>
            </th>
            <th ref={exchangeRef} className="py-3 px-4 text-left text-sm font-semibold relative group">
              Exchange Type
              <button
                onClick={handleToggleExchange}
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted inline-flex items-center"
              >
                {/* <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg> */}
              </button>
            </th>
            <th className="py-3 px-4 text-left text-sm font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {apis.length > 0 ? (
            apis.map((api, index) => (
              <tr
                key={api.ApiID}
                className={`border-b transition-colors duration-200 ${
                  index % 2 === 0 ? 'bg-card' : 'bg-background'
                } hover:bg-muted`}
              >
                <td className="py-3 px-4 text-xs text-foreground">{api.Created_At}</td>
                <td className="py-3 px-4 text-xs text-foreground">{api.Api_Name}</td>
                <td className="py-3 px-4 text-xs text-foreground">{api.ApiID}</td>
                <td className="py-3 px-4 text-xs text-foreground">{api.Exchange}</td>
                <td className="py-3 px-4 text-xs text-foreground">{api.Trade_Type}</td>
                <td className="py-3 px-4 text-xs text-foreground">
                  {api.SPOT_Enabled ? 'SPOT' : api.FUTURES_Enabled ? 'FUTURES' : 'N/A'}
                </td>
                <td className="py-3 px-4 text-xs">
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      api.Status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {api.Status}
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="py-3 px-4 text-center text-sm text-muted-foreground">
                No APIs found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {renderTradeDropdown()}
      {renderExchangeDropdown()}
    </div>
  );

  const renderCards = () => (
    <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      {apis.length > 0 ? (
        apis.map((api) => (
          <div key={api.ApiID} className="bg-card text-card-foreground rounded-lg border border-border p-6 shadow-md hover:shadow-lg transition-shadow">
            <div className="space-y-4">
              <div className='flex items-center justify-between'>
                <p className="text-sm text-primary">Datetime</p>
                <h3 className="font-semibold text-foreground text-xs">{api.Created_At}</h3>
              </div>
              <div className='flex items-center justify-between'>
                <p className="text-sm text-primary">API Name</p>
                <h3 className="font-semibold text-foreground text-xs">{api.Api_Name}</h3>
              </div>
              <div className='flex items-center justify-between'>
                <p className="text-sm text-primary">API ID</p>
                <h3 className="font-semibold text-foreground text-xs">{api.ApiID}</h3>
              </div>
              <div className='flex items-center justify-between'>
                <p className="text-sm text-primary">Exchange</p>
                <h3 className="font-semibold text-foreground text-xs">{api.Exchange}</h3>
              </div>
              <div className='flex items-center justify-between'>
                <p className="text-sm text-primary">Trade Type</p>
                <h3 className="font-semibold text-foreground text-xs">{api.Trade_Type}</h3>
              </div>
              <div className='flex items-center justify-between'>
                <p className="text-sm text-primary">Exchange Type</p>
                <h3 className="font-semibold text-foreground text-xs">
                  {api.SPOT_Enabled ? 'SPOT' : api.FUTURES_Enabled ? 'FUTURES' : 'N/A'}
                </h3>
              </div>
              <div className='flex items-center justify-between'>
                <p className="text-sm text-primary">Status</p>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    api.Status === 'ACTIVE'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                  }`}
                >
                  {api.Status}
                </span>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="col-span-full text-center py-12 text-primary">
          No APIs found
        </div>
      )}
    </div>
  );

  return (
    <div className="p-5 lg:p-8 bg-background text-foreground min-h-screen">
      <h2 className="text-xl font-bold mb-4">API Configurations</h2>
      {renderFiltersMobile()}
      {renderFiltersModal()}
      {renderTable()}
      {renderCards()}
      {renderPagination()}
    </div>
  );
}

export default ApiTable;